import { Fragment } from 'react';
import type { To } from 'react-router';
import { AddIcon } from '#assets/ReactSvg.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { environmentVariables } from '#global/styles/environment-variables.json';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { AddSplitButton, type AddMenuItem } from './add-split-button/AddSplitButton.tsx';
import { FilterPills } from './filter-pill/FilterPills.tsx';
import styles from './HeaderButtons.module.css';

type Props = {
  addTo?: To;
  /** Visible + accessible label of the add control. */
  addLabel?: string;
  /** When provided, the add control renders as a split button whose caret menu links here. */
  addMenuItems?: AddMenuItem[];
  alwaysGroupAdditional?: boolean;
  additionalButtons?: Array<{
    key: string;
    element: React.ReactNode;
    /** Keep this item at a readable floor in the collapsed group (e.g. a wide select). */
    primary?: boolean;
  }>;
  /**
   * Controls when the buttons render as the full-width icon ButtonGroup (the stacked-header
   * variant). Defaults to collapsing below `--size-breakpoint-xs`; pass a value when the page
   * stacks its header at a different point (see HostsPage) so both flip together.
   */
  collapsed?: boolean;
};

/**
 * HeaderButtons is either a set of buttons next to each other or a ButtonGroup,
 * depending on the width of the viewport.  An add button is always included,
 * and any other children are rendered next to it.
 */
export function HeaderButtons({
  addTo = 'add',
  addLabel = 'Add',
  addMenuItems,
  additionalButtons,
  alwaysGroupAdditional,
  collapsed: collapsedProp,
}: Props) {
  const location = useAppLocation();
  const isExtraNarrow = useMatchMedia(`(max-width: ${environmentVariables['--size-breakpoint-xs']})`);
  const collapsed = collapsedProp ?? isExtraNarrow;

  if (collapsed) {
    return (
      <div className={styles.HeaderButtons___collapsed}>
        <ButtonGroup stretch={true}>
          {additionalButtons?.map((item) => (
            <ButtonGroupItem key={item.key} className="width-100" primary={item.primary}>
              {item.element}
            </ButtonGroupItem>
          ))}
          {addMenuItems?.length ?
            <AddSplitButton addTo={addTo} addLabel={addLabel} menuItems={addMenuItems} collapsed={true} />
          : <ButtonGroupItem className="width-100">
              <Tooltip
                content={addLabel}
                placement="bottom"
                render={<LinkButton size="small" variant="icon" to={addTo} state={{ from: location }} />}
              >
                <span className="visually-hidden">{addLabel}</span>
                <AddIcon aria-hidden={true} />
              </Tooltip>
            </ButtonGroupItem>
          }
        </ButtonGroup>
        <FilterPills className="mt-24" />
      </div>
    );
  }

  return (
    <div className={styles.HeaderButtons___expanded}>
      <Spacer className={styles.HeaderButtons_buttons___right}>
        {alwaysGroupAdditional ?
          <ButtonGroup>
            {additionalButtons?.map((item) => (
              <ButtonGroupItem key={item.key}>{item.element}</ButtonGroupItem>
            ))}
          </ButtonGroup>
        : <div className="flex gap-0">
            {additionalButtons?.map((b) => (
              <Fragment key={b.key}>{b.element}</Fragment>
            ))}
          </div>
        }
        {addMenuItems?.length ?
          <AddSplitButton addTo={addTo} addLabel={addLabel} menuItems={addMenuItems} />
        : <LinkButton size="small" to={addTo} state={{ from: location }}>
            {addLabel}
          </LinkButton>
        }
      </Spacer>
    </div>
  );
}
